package com.evoapps.evoshift.ui.finance

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.Redeem
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.domain.model.Adjustment
import com.evoapps.evoshift.domain.model.AdjustmentType
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.usecase.finance.AdjustmentInput
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoConfirmDialog
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.formatEditable
import com.evoapps.evoshift.widgets.parseDecimal
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.adjustment_add
import evoshift.composeapp.generated.resources.adjustment_amount
import evoshift.composeapp.generated.resources.adjustment_day
import evoshift.composeapp.generated.resources.adjustment_delete_confirm
import evoshift.composeapp.generated.resources.adjustment_description
import evoshift.composeapp.generated.resources.adjustment_description_hint
import evoshift.composeapp.generated.resources.adjustment_edit_title
import evoshift.composeapp.generated.resources.adjustment_hint
import evoshift.composeapp.generated.resources.adjustment_month_locked
import evoshift.composeapp.generated.resources.adjustment_no_shift
import evoshift.composeapp.generated.resources.adjustment_none
import evoshift.composeapp.generated.resources.adjustment_shift
import evoshift.composeapp.generated.resources.adjustment_type
import evoshift.composeapp.generated.resources.adjustment_type_bonus
import evoshift.composeapp.generated.resources.adjustment_type_deduction
import evoshift.composeapp.generated.resources.adjustment_type_refund
import evoshift.composeapp.generated.resources.adjustments_title
import evoshift.composeapp.generated.resources.delete
import evoshift.composeapp.generated.resources.edit
import evoshift.composeapp.generated.resources.error_one_off_amount
import evoshift.composeapp.generated.resources.field_required
import evoshift.composeapp.generated.resources.number_invalid
import evoshift.composeapp.generated.resources.save
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import org.jetbrains.compose.resources.stringResource

/*
 * SH-78: a person's bonuses, deductions and expense refunds of the month, inside their estimate on
 * the labour cost screen. Owners and financial managers add, change and delete them (while the
 * plan runs and the month is not LOCKED); everyone else who sees the estimate reads them.
 */

/** "Bonus", "Deduction", "Expense refund". */
@Composable
fun adjustmentTypeLabel(type: AdjustmentType): String = stringResource(
    when (type) {
        AdjustmentType.BONUS -> Res.string.adjustment_type_bonus
        AdjustmentType.DEDUCTION -> Res.string.adjustment_type_deduction
        AdjustmentType.EXPENSE_REFUND -> Res.string.adjustment_type_refund
    }
)

/** The amount as it moves the pay: "+100,00 zł" for a bonus or refund, "−20,00 zł" for a deduction. */
fun signedAdjustmentAmount(adjustment: Adjustment, currency: String): String =
    (if (adjustment.type == AdjustmentType.DEDUCTION) "−" else "+") + formatMoney(adjustment.amount, adjustment.currency.ifBlank { currency })

/** "2026-02-14 (Sat) 08:00–16:00": a shift as the related-shift menu and the list name it. */
@Composable
internal fun adjustmentShiftLabel(shift: Shift): String {
    val day = runCatching { LocalDate.parse(shift.date) }.getOrNull()
    val times = "${shift.plannedTime.start.substringAfter('T').take(5)}–${shift.plannedTime.end.substringAfter('T').take(5)}"
    return if (day != null) "${costDayLabel(day)} $times" else "${shift.date} $times"
}

/**
 * The person's adjustments of the month: one line each (kind, description, day and related
 * shift, signed amount) with edit and delete when [canEdit], "Add bonus or deduction" below.
 * A [locked] month says so instead of offering changes.
 */
@Composable
fun AdjustmentsSection(
    adjustments: List<Adjustment>,
    shifts: List<Shift>,
    currency: String,
    canEdit: Boolean,
    locked: Boolean,
    onAdd: () -> Unit,
    onEdit: (Adjustment) -> Unit,
    onDelete: (Adjustment) -> Unit
) {
    Column(Modifier.fillMaxWidth().padding(top = 12.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(
            stringResource(Res.string.adjustments_title),
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = DeepNavy
        )
        if (adjustments.isEmpty()) {
            Text(stringResource(Res.string.adjustment_none), style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.7f))
        }
        val shiftsById = shifts.associateBy { it.id }
        adjustments.forEach { adjustment ->
            AdjustmentRow(adjustment, shiftsById[adjustment.relatedShiftId], currency, canEdit, onEdit, onDelete)
        }
        if (locked) {
            Row(Modifier.padding(top = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Default.Lock, null, tint = DeepNavy.copy(alpha = 0.6f), modifier = Modifier.size(16.dp))
                Spacer(Modifier.width(6.dp))
                Text(stringResource(Res.string.adjustment_month_locked), style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.7f))
            }
        } else if (canEdit) {
            OutlinedButton(onClick = onAdd, modifier = Modifier.fillMaxWidth().padding(top = 4.dp).handCursor(), shape = RoundedCornerShape(12.dp)) {
                Icon(Icons.Default.Add, contentDescription = null, tint = DeepNavy)
                Spacer(Modifier.width(8.dp))
                Text(stringResource(Res.string.adjustment_add), color = DeepNavy)
            }
        }
    }
}

@Composable
private fun AdjustmentRow(
    adjustment: Adjustment,
    shift: Shift?,
    currency: String,
    canEdit: Boolean,
    onEdit: (Adjustment) -> Unit,
    onDelete: (Adjustment) -> Unit
) {
    Row(Modifier.fillMaxWidth().padding(vertical = 2.dp), verticalAlignment = Alignment.CenterVertically) {
        Column(Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                AdjustmentTypeTag(adjustment.type)
                Spacer(Modifier.width(8.dp))
                Text(adjustment.description, style = MaterialTheme.typography.bodyMedium, color = DeepNavy, modifier = Modifier.weight(1f, fill = false))
            }
            val day = runCatching { LocalDate.parse(adjustment.date) }.getOrNull()?.let { costDayLabel(it) } ?: adjustment.date
            Text(
                listOfNotNull(day, shift?.let { adjustmentShiftLabel(it).substringAfter(") ") }).joinToString(" · "),
                style = MaterialTheme.typography.bodySmall,
                color = DeepNavy.copy(alpha = 0.7f)
            )
        }
        Text(signedAdjustmentAmount(adjustment, currency), fontWeight = FontWeight.SemiBold, color = DeepNavy)
        if (canEdit) {
            IconButtonWithTooltip(onClick = { onEdit(adjustment) }, modifier = Modifier.handCursor(), tooltip = stringResource(Res.string.edit)) {
                Icon(Icons.Default.Edit, contentDescription = stringResource(Res.string.edit), modifier = Modifier.size(20.dp), tint = DeepNavy)
            }
            IconButtonWithTooltip(onClick = { onDelete(adjustment) }, modifier = Modifier.handCursor(), tooltip = stringResource(Res.string.delete)) {
                Icon(Icons.Default.Delete, contentDescription = stringResource(Res.string.delete), modifier = Modifier.size(20.dp), tint = MaterialTheme.colorScheme.error)
            }
        }
    }
}

/** The kind as a small tag: gold for money in, navy for a deduction. */
@Composable
private fun AdjustmentTypeTag(type: AdjustmentType) {
    val deduction = type == AdjustmentType.DEDUCTION
    Text(
        adjustmentTypeLabel(type),
        style = MaterialTheme.typography.labelSmall,
        color = if (deduction) Color.White else DeepNavy,
        modifier = Modifier.clip(RoundedCornerShape(6.dp))
            .background(if (deduction) DeepNavy.copy(alpha = 0.7f) else DarkGold)
            .padding(horizontal = 6.dp, vertical = 2.dp)
    )
}

/**
 * Adding (no [adjustment]) or changing a bonus, deduction or expense refund of the month
 * [year]/[month]: the kind, a positive amount in [currency], a day of the month, a description and
 * optionally one of the person's [shifts] it is for.
 */
@Composable
fun AdjustmentDialog(
    year: Int,
    month: Int,
    currency: String,
    shifts: List<Shift>,
    adjustment: Adjustment?,
    onDismiss: () -> Unit,
    onSave: (AdjustmentInput) -> Unit
) {
    val first = LocalDate(year, month, 1)
    val days = remember(year, month) { generateSequence(first) { it.plus(DatePeriod(days = 1)) }.takeWhile { it.month == first.month }.toList() }
    var type by remember { mutableStateOf(adjustment?.type ?: AdjustmentType.BONUS) }
    var amountText by remember { mutableStateOf(adjustment?.amount?.let { formatEditable(it) }.orEmpty()) }
    var day by remember { mutableStateOf(adjustment?.date?.let { runCatching { LocalDate.parse(it) }.getOrNull() }?.takeIf { it in days } ?: first) }
    var description by remember { mutableStateOf(adjustment?.description.orEmpty()) }
    var shiftId by remember { mutableStateOf(adjustment?.relatedShiftId?.takeIf { id -> shifts.any { it.id == id } }) }
    var tried by remember { mutableStateOf(false) }

    val amount = parseDecimal(amountText)
    val required = stringResource(Res.string.field_required)
    val amountError = when {
        amountText.isNotBlank() && amount == null -> stringResource(Res.string.number_invalid)
        amount != null && amount <= 0.0 -> stringResource(Res.string.error_one_off_amount)
        tried && amountText.isBlank() -> required
        else -> null
    }
    val valid = description.isNotBlank() && amount != null && amount > 0.0
    val shiftsById = shifts.associateBy { it.id }
    val noShift = stringResource(Res.string.adjustment_no_shift)

    EvoDialog(
        title = stringResource(if (adjustment == null) Res.string.adjustment_add else Res.string.adjustment_edit_title),
        icon = Icons.Default.Redeem,
        onDismiss = onDismiss,
        confirm = DialogAction(
            stringResource(Res.string.save),
            {
                tried = true
                if (valid) onSave(AdjustmentInput(type, amount, day.toString(), description.trim(), shiftId))
            }
        )
    ) {
        EvoDropdown(
            label = stringResource(Res.string.adjustment_type),
            options = AdjustmentType.entries,
            selected = type,
            onSelected = { type = it },
            optionLabel = { adjustmentTypeLabel(it) },
            supportingText = stringResource(Res.string.adjustment_hint)
        )
        EvoTextField(
            value = amountText,
            onValueChange = { amountText = it },
            label = stringResource(Res.string.adjustment_amount, currency),
            keyboardType = KeyboardType.Decimal,
            suffix = currency,
            error = amountError
        )
        EvoDropdown(
            label = stringResource(Res.string.adjustment_day),
            options = days,
            selected = day,
            onSelected = { day = it },
            optionLabel = { costDayLabel(it) }
        )
        EvoTextField(
            value = description,
            onValueChange = { description = it },
            label = stringResource(Res.string.adjustment_description),
            placeholder = stringResource(Res.string.adjustment_description_hint),
            error = required.takeIf { tried && description.isBlank() },
            imeAction = ImeAction.Done
        )
        if (shifts.isNotEmpty()) {
            EvoDropdown(
                label = stringResource(Res.string.adjustment_shift),
                options = listOf<String?>(null) + shifts.map { it.id },
                selected = shiftId,
                onSelected = { picked ->
                    shiftId = picked
                    // A shift on another day moves the adjustment to that day.
                    picked?.let { id -> shiftsById[id]?.date?.let { runCatching { LocalDate.parse(it) }.getOrNull() } }
                        ?.takeIf { it in days }?.let { day = it }
                },
                optionLabel = { id -> id?.let { shiftsById[it] }?.let { adjustmentShiftLabel(it) } ?: noShift }
            )
        }
    }
}

/** Removing a bonus, deduction or refund from the month's estimate. */
@Composable
fun DeleteAdjustmentDialog(adjustment: Adjustment, onConfirm: () -> Unit, onDismiss: () -> Unit) {
    EvoConfirmDialog(
        title = stringResource(Res.string.delete),
        text = stringResource(Res.string.adjustment_delete_confirm, adjustment.description),
        confirmLabel = stringResource(Res.string.delete),
        onConfirm = onConfirm,
        onDismiss = onDismiss,
        destructive = true,
        icon = Icons.Default.Delete
    )
}
